import {
  CameraDevice,
  Customer,
  CustomerFleetSummary,
  AlertItem,
  FaceCapture,
  SimCard,
  Subscription,
  PaymentRecord,
  PlanItem,
  InventoryItem,
  SupportTicket,
  SupportTicketMessage,
  SupportTicketStatus,
  StaffAdmin,
  PlatformRole,
  AnalyticsData,
  CloudStorageList,
  CloudStorageCamera,
  CloudRegionsResult,
  CloudVerdict,
  RecordingEvent,
  CameraInsights,
  AIUsageSummary,
  StoreOrder,
  StoreOrderList,
  EmergencyEvent,
  AdminUser,
  AuditLogEntry,
  BroadcastMessage,
  BroadcastSegment,
  AdminRole,
  OfflineTriageList,
  OfflineTriageCamera,
  TriageReason,
  CustomerDetail,
  FleetVersion,
  FleetVersionSummary,
} from '../types';
import {
  MOCK_DEVICES,
  MOCK_CUSTOMERS,
  MOCK_CUSTOMER_FLEETS,
  MOCK_ALERTS,
  MOCK_SUBSCRIPTIONS,
  MOCK_PAYMENTS,
  MOCK_PLANS,
  MOCK_INVENTORY,
  MOCK_EMERGENCIES,
} from './mockData';

// All calls go through the gateway. In prod the static build sits behind the same
// edge as the gateway, so the default relative '/v2' is same-origin. In dev, set
// VITE_API_BASE (e.g. https://api.my-watchman.com/v2) to hit the gateway directly
// (its CORS is permissive), avoiding the dev proxy.
const API_BASE = ((import.meta as any).env?.VITE_API_BASE as string) || '/v2';

const ACCESS_KEY = 'mwm_admin_access';
const REFRESH_KEY = 'mwm_admin_refresh';

// Demo mode returns the bundled sample data instead of calling the backend.
// OFF by default — turn on with VITE_DEMO=1 at build/dev time, or
// localStorage.setItem('mwm_demo','1') in the browser. Never the default.
function demoMode(): boolean {
  try {
    if ((import.meta as any).env?.VITE_DEMO === '1') return true;
    return localStorage.getItem('mwm_demo') === '1';
  } catch {
    return false;
  }
}

/** Thrown when a spec [BUILD] endpoint has no backend yet — the UI renders a
 *  clear "backend pending" state rather than fabricating data. */
export class NotImplementedError extends Error {
  endpoint: string;
  constructor(endpoint: string) {
    super(`Backend endpoint not yet available: ${endpoint}`);
    this.name = 'NotImplementedError';
    this.endpoint = endpoint;
  }
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Modules whose backend endpoint does not exist yet (spec[BUILD]). Used by the
// UI to show an honest "endpoint pending" banner instead of empty/fake tables.
// Only pages with NO working backend belong here. Pages that now work — even by
// deriving from another real endpoint (subscriptions from customer records) or
// carrying their own in-page honesty note (sim-management: the 4G fleet is real,
// only the SIM metering is absent) — must NOT be listed, or the banner lies.
// Every admin module now has a real backend. Kept as the seam for honestly
// flagging any future not-yet-built module.
export const PENDING_MODULES: Record<string, string> = {};

// ---- helpers ---------------------------------------------------------------

function unwrap(json: any): any {
  // Most services wrap as { success, data: {...} }; some return the payload flat.
  return json && typeof json === 'object' && 'data' in json ? json.data : json;
}

function fullName(first?: string, last?: string, fallback = ''): string {
  const n = [first, last].filter(Boolean).join(' ').trim();
  return n || fallback;
}

function pickRole(roles: string[] = []): AdminRole {
  if (roles.includes('super_admin')) return 'super_admin';
  if (roles.includes('admin')) return 'admin';
  return 'operator'; // least-privileged staff tier (establish() gates non-staff)
}

// The staff roles allowed to sign in to this console at all.
export function isStaffRole(roles: string[] = []): boolean {
  return roles.includes('super_admin') || roles.includes('admin') || roles.includes('operator');
}

// Support ticket mappers — snake_case backend rows → camelCase client shape.
function mapTicketMessage(m: any): SupportTicketMessage {
  return {
    id: m.id,
    authorId: m.author_id,
    authorRole: m.author_role,
    body: m.body,
    createdAt: m.created_at,
    authorName: m.author_name || undefined,
  };
}

function mapBroadcast(b: any): BroadcastMessage {
  return {
    id: b.id,
    title: b.title,
    body: b.body,
    audience: b.audience ?? 'all',
    channels: b.channels ?? 'push',
    sentBy: b.sent_by ?? '',
    sentByEmail: b.sent_by_email ?? '',
    recipientCount: b.recipient_count ?? 0,
    status: b.status ?? 'sent',
    createdAt: b.created_at,
  };
}

function mapTicket(t: any): SupportTicket {
  return {
    id: t.id,
    customerId: t.customer_id,
    deviceId: t.device_id || undefined,
    subject: t.subject,
    reason: t.reason || undefined,
    status: t.status,
    origin: t.origin,
    assignedAgentId: t.assigned_agent_id || undefined,
    assignedAgentEmail: t.assigned_agent_email || undefined,
    assignedAgentName: t.assigned_agent_name || undefined,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    resolvedAt: t.resolved_at || undefined,
    customerName: t.customer_name || undefined,
    customerEmail: t.customer_email || undefined,
    customerPhone: t.customer_phone || undefined,
    deviceName: t.device_name || undefined,
    deviceSerial: t.device_serial || undefined,
    messageCount: t.message_count ?? 0,
    lastMessageAt: t.last_message_at || undefined,
    deviceOnline: typeof t.device_online === 'boolean' ? t.device_online : undefined,
    deviceLastSeen: t.device_last_seen || undefined,
    messages: Array.isArray(t.messages) ? t.messages.map(mapTicketMessage) : undefined,
  };
}

class ApiClient {
  private access: string | null = null;
  private refresh: string | null = null;
  private currentAdminEmail = '';
  private currentAdminId = '';

  constructor() {
    try {
      this.access = localStorage.getItem(ACCESS_KEY);
      this.refresh = localStorage.getItem(REFRESH_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  isDemo(): boolean {
    return demoMode();
  }

  private setTokens(access: string | null, refresh: string | null) {
    this.access = access;
    this.refresh = refresh;
    try {
      if (access) localStorage.setItem(ACCESS_KEY, access);
      else localStorage.removeItem(ACCESS_KEY);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
      else localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
  }

  hasSession(): boolean {
    return !!this.access;
  }

  private headers(): HeadersInit {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.access) h['Authorization'] = `Bearer ${this.access}`;
    return h;
  }

  /** fetch wrapper: attaches the bearer, refreshes once on 401, throws ApiError. */
  private async request(method: string, path: string, body?: unknown): Promise<any> {
    const doFetch = () =>
      fetch(`${API_BASE}${path}`, {
        method,
        headers: this.headers(),
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

    let res = await doFetch();
    if (res.status === 401 && this.refresh) {
      if (await this.doRefresh()) res = await doFetch();
    }
    const text = await res.text();
    let json: any = {};
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        // Some error responses are plain text (e.g. "page_size must be…") — don't crash.
        json = { _raw: text };
      }
    }
    if (!res.ok) {
      const msg = json?.error || json?.message || json?._raw || `HTTP ${res.status}`;
      throw new ApiError(res.status, msg);
    }
    return json;
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refresh }),
      });
      if (!res.ok) {
        this.setTokens(null, null);
        return false;
      }
      const payload = unwrap(await res.json());
      this.setTokens(payload.access_token, payload.refresh_token ?? this.refresh);
      return true;
    } catch {
      this.setTokens(null, null);
      return false;
    }
  }

  // ==========================================================================
  // AUTH
  // ==========================================================================

  /** Password step. Returns an MFA challenge OR establishes the session. */
  async login(
    email: string,
    password: string,
  ): Promise<{ mfaRequired: boolean; mfaToken?: string; user?: AdminUser }> {
    const json = await this.request('POST', '/auth/login', { email, password });
    const payload = unwrap(json);
    if (payload?.mfa_required) {
      return { mfaRequired: true, mfaToken: payload.mfa_token };
    }
    return { mfaRequired: false, user: this.establish(payload) };
  }

  /** Exchanges an MFA challenge + code for a session. */
  async verifyMfaLogin(mfaToken: string, code: string): Promise<AdminUser> {
    const json = await this.request('POST', '/auth/mfa/verify-login', {
      mfa_token: mfaToken,
      code,
    });
    return this.establish(unwrap(json));
  }

  /** Stores tokens and enforces the admin-role gate (identically to both paths). */
  private establish(payload: any): AdminUser {
    const { access_token, refresh_token, user } = payload ?? {};
    if (!access_token || !user) throw new ApiError(500, 'Invalid response from server');
    const roles: string[] = user.roles ?? [];
    if (!isStaffRole(roles)) {
      throw new ApiError(403, 'Access denied. This console is for platform staff only.');
    }
    this.setTokens(access_token, refresh_token);
    return this.mapUser(user);
  }

  /** Re-validate the stored session against the backend on app load. */
  async loadProfile(): Promise<AdminUser | null> {
    if (!this.access) return null;
    try {
      const json = await this.request('GET', '/auth/profile');
      const user = unwrap(json);
      const roles: string[] = user?.roles ?? [];
      if (!isStaffRole(roles)) {
        this.setTokens(null, null);
        return null;
      }
      return this.mapUser(user);
    } catch {
      this.setTokens(null, null);
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.request('POST', '/auth/logout');
    } catch {
      /* best-effort */
    }
    this.setTokens(null, null);
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<{ success: boolean }> {
    await this.request('POST', '/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
    return { success: true };
  }

  /** Begin 2FA enrollment — returns the TOTP secret, an otpauth:// URL, and
   *  one-time recovery codes. Not active until verifyMfa() with a valid code. */
  async enableMfa(): Promise<{ secret: string; otpauthUrl: string; recoveryCodes: string[] }> {
    const d = unwrap(await this.request('POST', '/auth/enable-mfa')) as any;
    return {
      secret: String(d?.secret ?? ''),
      otpauthUrl: String(d?.otpauth_url ?? ''),
      recoveryCodes: Array.isArray(d?.recovery_codes) ? d.recovery_codes.map(String) : [],
    };
  }
  /** Confirm enrollment with a TOTP code — activates 2FA. */
  async verifyMfa(code: string): Promise<void> {
    await this.request('POST', '/auth/verify-mfa', { code });
  }
  /** Turn 2FA off — requires a current TOTP code. */
  async disableMfa(code: string): Promise<void> {
    await this.request('POST', '/auth/disable-mfa', { code });
  }

  private mapUser(u: any): AdminUser {
    const role = pickRole(u.roles);
    this.currentAdminEmail = u.email;
    this.currentAdminId = u.id;
    return {
      id: u.id,
      email: u.email,
      name: fullName(u.first_name, u.last_name, u.email),
      role,
      permissions: [], // AuthContext fills from ROLE_PERMISSIONS[role]
      mfaEnabled: !!u.mfa_enabled,
      lastLogin: u.last_login_at,
      createdAt: u.created_at,
      isActive: !!u.is_active,
    };
  }

  // ==========================================================================
  // FLEET — real reads
  // ==========================================================================

  async getDevices(): Promise<CameraDevice[]> {
    if (demoMode()) return [...MOCK_DEVICES];
    const d = unwrap(await this.request('GET', '/admin/devices?page_size=100'));
    const rows: any[] = d.devices ?? d ?? [];
    return rows.map((r) => this.mapDevice(r));
  }

  /** All cameras of a connection type ("4g" | "wifi"), paging past the 100-row
   *  cap so the SIM / 4G page sees the whole set. */
  async getDevicesByConnection(connectionType: '4g' | 'wifi'): Promise<CameraDevice[]> {
    if (demoMode()) return [];
    const out: CameraDevice[] = [];
    for (let page = 1; page <= 20; page++) {
      const d = unwrap(await this.request('GET', `/admin/devices?connection_type=${connectionType}&page_size=100&page=${page}`));
      const rows: any[] = d.devices ?? [];
      out.push(...rows.map((r) => this.mapDevice(r)));
      const total = d.pagination?.total_items ?? out.length;
      if (out.length >= total || rows.length === 0) break;
    }
    return out;
  }

  /** Only the cameras that have been geo-pinned (via the SOS flow). Very few
   *  today — the map is honest about that. Uses the backend has_location filter
   *  so it isn't limited to the first page of the fleet. */
  async getLocatedDevices(): Promise<CameraDevice[]> {
    if (demoMode()) return MOCK_DEVICES.filter((d) => d.hasLocation);
    const d = unwrap(await this.request('GET', '/admin/devices?has_location=true&page_size=100'));
    const rows: any[] = d.devices ?? d ?? [];
    return rows.map((r) => this.mapDevice(r));
  }

  private mapDevice(r: any): CameraDevice {
    const online = !!r.is_online;
    const status: CameraDevice['status'] = online
      ? 'online'
      : r.connection_status === 'error' || r.connection_status === 'degraded'
        ? 'degraded'
        : 'offline';
    return {
      id: r.id,
      serial: r.serial_number ?? '',
      name: r.name ?? r.serial_number ?? 'Camera',
      // Whitelabel: show the (rebranded) model — never the OEM manufacturer name.
      model: r.model ?? '',
      ownerId: r.owner?.id ?? r.user_id ?? '',
      ownerName: fullName(r.owner?.first_name, r.owner?.last_name, r.owner?.email ?? ''),
      ownerEmail: r.owner?.email ?? '',
      status,
      connectionType: r.connection_type ?? undefined,
      eye4State: r.eye4_state ?? undefined,
      vuid: r.vuid ?? null,
      vuidState: r.vuid ? 'resolved' : 'empty',
      relayCluster: r.vuid_cluster ?? '',
      relayHost: '',
      isRelayReachable: online,
      hasLocation: r.latitude != null && r.longitude != null,
      latitude: r.latitude ?? undefined,
      longitude: r.longitude ?? undefined,
      address: r.location_name ?? undefined,
      siteId: r.site_id ?? undefined,
      siteName: r.site_name ?? undefined,
      lastHeartbeat: r.last_seen_at ?? r.updated_at ?? '',
      isBlocked: !!r.is_blocked,
      firmwareVersion: r.firmware_version ?? '',
      sdkVersion: r.sdk_version ?? '',
      cloudRecording: {
        isRecording: !!r.cloud_storage_enabled,
        lastClipTime: r.last_clip_at ?? null,
        retentionDays: r.recording_retention_days ?? 0,
        status: 'healthy',
      },
    };
  }

  async getCustomerFleets(): Promise<CustomerFleetSummary[]> {
    if (demoMode()) return [...MOCK_CUSTOMER_FLEETS];
    const d = unwrap(await this.request('GET', '/admin/customer-fleets?page_size=100'));
    const rows: any[] = d.customers ?? d ?? [];
    return rows.map((r) => ({
      customerId: r.customer_id,
      customerName: fullName(r.first_name, r.last_name, r.email),
      customerEmail: r.email ?? '',
      customerPhone: r.phone ?? '',
      totalCameras: r.cameras ?? 0,
      onlineCameras: r.online ?? 0,
      offlineCameras: r.offline ?? 0,
      isSiteFullyDown: (r.cameras ?? 0) > 0 && (r.online ?? 0) === 0,
      planName: r.plan_name ?? '—',
      offlineDevices: (r.down ?? []).map((x: any) => ({
        deviceId: x.id,
        serial: x.serial_number ?? '',
        name: x.name ?? '',
        lastSeenAt: x.last_seen_at ?? null,
        reason: x.reason || undefined,
        eye4State: x.eye4_state || undefined,
        namePhone: x.name_phone || undefined,
      })),
    }));
  }

  /** Accurate platform-wide fleet counts from customer-fleets `totals` (not
   *  capped by page_size like listing the devices would be). */
  async getFleetTotals(): Promise<{
    cameras: number;
    online: number;
    offline: number;
    sitesDown: number;
    customersWithCameras: number;
    inactivated: number;
    neverReported: number;
    silent: number;
    silentReachable: number;
    silentDead: number;
  }> {
    if (demoMode()) {
      const online = MOCK_DEVICES.filter((d) => d.status === 'online').length;
      return {
        cameras: MOCK_DEVICES.length,
        online,
        offline: MOCK_DEVICES.length - online,
        sitesDown: MOCK_CUSTOMER_FLEETS.filter((f) => f.isSiteFullyDown).length,
        customersWithCameras: MOCK_CUSTOMER_FLEETS.length,
        inactivated: 0,
        neverReported: 0,
        silent: 0,
        silentReachable: 0,
        silentDead: 0,
      };
    }
    const d = unwrap(await this.request('GET', '/admin/customer-fleets?page_size=1'));
    const t = d.totals ?? {};
    return {
      cameras: t.cameras ?? 0,
      online: t.online ?? 0,
      offline: t.offline ?? 0,
      sitesDown: t.customers_all_down ?? 0,
      customersWithCameras: t.customers ?? 0,
      inactivated: t.inactivated ?? 0,
      neverReported: t.never_reported ?? 0,
      silent: t.silent ?? 0,
      silentReachable: t.silent_reachable ?? 0,
      silentDead: t.silent_dead ?? 0,
    };
  }

  async getAlerts(): Promise<AlertItem[]> {
    if (demoMode()) return [...MOCK_ALERTS];
    const json = await this.request('GET', '/admin/alerts?limit=100');
    const rows: any[] = json.alerts ?? unwrap(json)?.alerts ?? [];
    const typeMap: Record<string, AlertItem['type']> = {
      motion: 'motion',
      person: 'motion',
      offline: 'offline',
      tamper: 'tamper',
      sos: 'sos',
    };
    return rows.map((r) => ({
      id: r.id,
      deviceId: r.detection_id ?? r.source_event_id ?? '',
      deviceSerial: r.vuid ?? '',
      deviceName: r.device_name ?? '',
      ownerName: r.owner_name ?? r.owner_email ?? '',
      type: typeMap[(r.label ?? '').toLowerCase()] ?? 'motion',
      severity:
        r.severity === 'critical' || r.severity === 'high'
          ? 'critical'
          : r.severity === 'warning' || r.severity === 'medium'
            ? 'warning'
            : 'info',
      status: r.acknowledged ? 'acknowledged' : 'active',
      timestamp: r.created_at ?? '',
      message: r.title || r.body || r.label || 'Alert',
      coverSnapshotUrl: r.payload?.picture_url ?? undefined,
    }));
  }

  /** Faces gallery: recent alerts that carry a stored face crop (face.jpg),
   *  newest first. Pulls the stranger ("unknown_person") stream and keeps the
   *  rows that actually have a face crop, resolving both the tight face image
   *  and the wider scene cover to media-proxy URLs. days bounds the look-back. */
  async listFaceCaptures(opts?: { days?: number; limit?: number }): Promise<FaceCapture[]> {
    const limit = opts?.limit ?? 200;
    const json = await this.request('GET', `/admin/alerts?label=unknown_person&limit=${limit}`);
    const rows: any[] = json.alerts ?? unwrap(json)?.alerts ?? [];
    const cutoff = Date.now() - (opts?.days ?? 7) * 86400000;
    return rows
      .filter((r) => r?.payload?.face_key && new Date(r.created_at).getTime() >= cutoff)
      .map((r) => ({
        id: r.id,
        vuid: r.vuid ?? '',
        label: r.label ?? 'unknown_person',
        at: r.created_at ?? '',
        known: (r.label ?? '') !== 'unknown_person',
        name: r.payload?.face_name ?? undefined,
        faceUrl: this.mediaUrl(r.payload.face_key),
        coverUrl: r.payload?.cover_key
          ? this.mediaUrl(r.payload.cover_key)
          : (r.payload?.picture_url ?? undefined),
      }));
  }

  // ==========================================================================
  // CUSTOMERS & BILLING — real reads
  // ==========================================================================

  private mapCustomerRow(r: any): Customer {
    return {
      id: r.id,
      name: fullName(r.first_name, r.last_name, r.email),
      email: r.email ?? '',
      phone: r.phone_number ?? r.phone ?? '',
      joinedAt: r.created_at ?? '',
      cameraCount: r.device_count ?? 0,
      // Plan + status are nested under `subscription` (nil for customers who have
      // never subscribed — most of them), not flat on the row.
      activePlan: r.subscription?.plan_name ?? r.plan_name ?? '—',
      billingStatus: ((r.subscription?.status ?? r.billing_status) as Customer['billingStatus']) ?? 'none',
      renewalDate: r.subscription?.current_period_end ?? r.renewal_date ?? '',
      isBlocked: !!r.is_blocked,
      lastLoginAt: r.last_login_at,
    };
  }

  /** Every customer, paging past the 100-row cap. The fleet is small enough
   *  (~hundreds) to hold client-side, so search/filter cover everyone — not just
   *  the newest 100, which is why a customer like "Marline" read as not found. */
  async getAllCustomers(): Promise<Customer[]> {
    if (demoMode()) return [...MOCK_CUSTOMERS];
    const out: Customer[] = [];
    for (let page = 1; page <= 50; page++) {
      const d = unwrap(await this.request('GET', `/admin/customers?page_size=100&page=${page}`));
      const rows: any[] = d.customers ?? [];
      out.push(...rows.map((r) => this.mapCustomerRow(r)));
      const total = d.pagination?.total_items ?? out.length;
      if (out.length >= total || rows.length === 0) break;
    }
    return out;
  }

  async getCustomers(): Promise<Customer[]> {
    if (demoMode()) return [...MOCK_CUSTOMERS];
    const d = unwrap(await this.request('GET', '/admin/customers?page_size=100'));
    const rows: any[] = d.customers ?? d ?? [];
    return rows.map((r) => this.mapCustomerRow(r));
  }

  /** Server-side customer search (email / name / phone). Finds anyone regardless
   *  of how many customers exist or which page they'd be on. */
  async searchCustomers(term: string): Promise<Customer[]> {
    if (demoMode()) {
      const t = term.toLowerCase();
      return MOCK_CUSTOMERS.filter((c) => (c.name + c.email).toLowerCase().includes(t));
    }
    const q = encodeURIComponent(term.trim());
    const d = unwrap(await this.request('GET', `/admin/customers?search=${q}&page_size=100`));
    const rows: any[] = d.customers ?? [];
    return rows.map((r) => this.mapCustomerRow(r));
  }

  /** Soft-deleted accounts (deleted=true), for the restore view. Returns empty
   *  in demo mode — there are no deleted mock accounts. */
  async getDeletedCustomers(): Promise<Customer[]> {
    if (demoMode()) return [];
    const out: Customer[] = [];
    for (let page = 1; page <= 50; page++) {
      const d = unwrap(await this.request('GET', `/admin/customers?deleted=true&page_size=100&page=${page}`));
      const rows: any[] = d.customers ?? [];
      out.push(...rows.map((r) => this.mapCustomerRow(r)));
      const total = d.pagination?.total_items ?? out.length;
      if (out.length >= total || rows.length === 0) break;
    }
    return out;
  }

  /** Search within soft-deleted accounts (email / name / phone). */
  async searchDeletedCustomers(term: string): Promise<Customer[]> {
    if (demoMode()) return [];
    const q = encodeURIComponent(term.trim());
    const d = unwrap(await this.request('GET', `/admin/customers?deleted=true&search=${q}&page_size=100`));
    const rows: any[] = d.customers ?? [];
    return rows.map((r) => this.mapCustomerRow(r));
  }

  async getCustomerSummary(): Promise<{
    totalCustomers: number;
    activePlans: number;
    mrr: number;
    currency: string;
    byStatus: Record<string, number>;
  }> {
    if (demoMode()) {
      return {
        totalCustomers: MOCK_CUSTOMERS.length,
        activePlans: MOCK_CUSTOMERS.filter((c) => c.billingStatus === 'active').length,
        mrr: 0,
        currency: 'USD',
        byStatus: {},
      };
    }
    const d = unwrap(await this.request('GET', '/admin/customers/summary'));
    return {
      totalCustomers: d.total ?? 0,
      activePlans: d.active_plan_count ?? d.by_status?.active ?? 0,
      mrr: Number(d.monthly_recurring ?? 0),
      currency: d.currency ?? 'USD',
      byStatus: d.by_status ?? {},
    };
  }

  async getPayments(): Promise<PaymentRecord[]> {
    if (demoMode()) return [...MOCK_PAYMENTS];
    const d = unwrap(await this.request('GET', '/admin/payments?page_size=100'));
    const rows: any[] = d.payments ?? d ?? [];
    const statusMap: Record<string, PaymentRecord['status']> = {
      succeeded: 'succeeded',
      success: 'succeeded',
      completed: 'succeeded',
      failed: 'failed',
      refunded: 'refunded',
      pending: 'pending',
    };
    const methodMap: Record<string, PaymentRecord['method']> = {
      cash: 'cash',
      momo: 'momo',
      bank_transfer: 'bank_transfer',
      card: 'card',
    };
    return rows.map((r) => ({
      id: r.id,
      customerId: r.customer_id ?? '',
      customerName: r.customer_name || r.customer_email || 'Customer',
      planName: r.plan_name ?? '—',
      amountUsd: Number(r.amount ?? 0),
      status: statusMap[(r.status ?? '').toLowerCase()] ?? 'pending',
      method: methodMap[(r.provider ?? r.type ?? '').toLowerCase()] ?? 'card',
      provider: r.provider ?? undefined,
      reference: r.external_id ?? r.notes ?? '',
      createdAt: r.created_at ?? '',
    }));
  }

  async getPlans(): Promise<PlanItem[]> {
    if (demoMode()) return [...MOCK_PLANS];
    const json = await this.request('GET', '/plans');
    const rows: any[] = json.plans ?? unwrap(json)?.plans ?? [];
    return rows.map((p) => {
      const feats: any[] = p.features ?? [];
      const fv = (key: string) => feats.find((f) => f.feature_key === key)?.feature_value;
      const aiFeatures = feats
        .filter(
          (f) =>
            f.feature_value === 'true' &&
            f.feature_key !== 'cloud_cameras' &&
            f.feature_key !== 'cloud_storage_days',
        )
        .map((f) => f.feature_key);
      return {
        slug: p.slug,
        name: p.name,
        priceMonthlyUsd: Number(p.price_monthly ?? 0),
        priceYearlyUsd: Number(p.price_yearly ?? 0),
        cloudCameraLimit: Number(fv('cloud_cameras') ?? 0),
        retentionDays: Number(fv('cloud_storage_days') ?? 0),
        aiFeatures,
        isActive: !!p.is_active,
        rawFeatures: feats.map((f) => ({ key: f.feature_key, value: String(f.feature_value ?? '') })),
      };
    });
  }

  async getInventory(): Promise<InventoryItem[]> {
    if (demoMode()) return [...MOCK_INVENTORY];
    const d = unwrap(await this.request('GET', '/devices/inventory'));
    const rows: any[] = d.items ?? d ?? [];
    const catMap: Record<string, InventoryItem['category']> = {
      camera: 'camera',
      accessory: 'accessory',
      sim: 'sim',
      nvr: 'nvr',
    };
    return rows.map((r) => ({
      id: r.id,
      sku: r.sku ?? '',
      name: r.model_name ?? r.name ?? 'Item',
      model: r.model_name ?? '',
      quantityOnHand: r.quantity ?? 0,
      lowStockThreshold: r.low_stock_threshold ?? 0,
      unitCostUsd: Number(r.cost ?? 0),
      retailPriceUsd: Number(r.price ?? 0),
      imageUrl: r.image_url ?? undefined,
      category: catMap[(r.type ?? '').toLowerCase()] ?? 'accessory',
      updatedAt: r.updated_at ?? '',
    }));
  }

  // ==========================================================================
  // BUILD [pending backend] — honest failures, never fabricated success.
  // Each throws NotImplementedError so the UI shows the pending endpoint.
  // Demo mode still returns sample data so the prototype can be shown.
  // ==========================================================================

  async getDeviceDiagnostics(deviceId: string): Promise<CameraDevice> {
    if (demoMode()) return { ...(MOCK_DEVICES.find((d) => d.id === deviceId) ?? MOCK_DEVICES[0]) };
    throw new NotImplementedError('GET /v2/admin/devices/{id}/diagnostics');
  }
  async rebootDevice(_deviceId: string, _reason: string): Promise<{ success: boolean; message: string }> {
    throw new NotImplementedError('POST /v2/admin/devices/{id}/reboot');
  }
  async reprovisionDevice(_deviceId: string, _reason: string): Promise<{ success: boolean; message: string }> {
    throw new NotImplementedError('POST /v2/admin/devices/{id}/reprovision');
  }
  async resolveVuid(
    _deviceId: string,
  ): Promise<{ success: boolean; vuidState: string; vuid: string | null; message: string }> {
    throw new NotImplementedError('POST /v2/admin/devices/{id}/resolve-vuid');
  }
  async toggleDeviceBlock(
    _deviceId: string,
    _isBlocked: boolean,
    _reason: string,
  ): Promise<{ success: boolean }> {
    throw new NotImplementedError('POST /v2/admin/devices/{id}/block');
  }
  /** Real, data-backed triage: every offline camera with the one reason it is
   *  offline + evidence + owner. Optional reason filter pages one bucket; counts
   *  always describe the whole offline set. */
  async getOfflineTriage(
    reason?: TriageReason,
    page = 1,
    pageSize = 50,
  ): Promise<OfflineTriageList> {
    const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (reason) qs.set('reason', reason);
    const d = unwrap(await this.request('GET', `/admin/devices/offline-triage?${qs}`));
    const c = d.counts ?? {};
    const cameras: OfflineTriageCamera[] = (d.cameras ?? []).map((r: any) => ({
      id: r.id,
      name: r.name ?? '',
      serialNumber: r.serial_number ?? '',
      reason: r.reason,
      vuid: r.vuid || undefined,
      vuidCluster: r.vuid_cluster || undefined,
      eye4State: r.eye4_state || undefined,
      eye4CheckedAt: r.eye4_checked_at ?? null,
      lastSignalAt: r.last_signal_at ?? null,
      ownerName: r.owner_name || undefined,
      ownerEmail: r.owner_email ?? '',
      ownerPhone: r.owner_phone || undefined,
      namePhone: r.name_phone || undefined,
    }));
    return {
      counts: {
        unresolved: c.unresolved ?? 0,
        needsReactivation: c.needs_reactivation ?? 0,
        reachableQuiet: c.reachable_quiet ?? 0,
        neverReported: c.never_reported ?? 0,
        silentUnknown: c.silent_unknown ?? 0,
        total: c.total ?? 0,
      },
      cameras,
      pagination: {
        page: d.pagination?.page ?? page,
        pageSize: d.pagination?.page_size ?? pageSize,
        totalItems: d.pagination?.total_items ?? cameras.length,
        totalPages: d.pagination?.total_pages ?? 1,
      },
    };
  }

  /** Cloud-storage health: per-camera verdict (entitlement + cloud mode +
   *  liveness + clip activity). Optional verdict/search filters. */
  async getCloudStorage(
    verdict?: CloudVerdict,
    search?: string,
    page = 1,
    pageSize = 50,
  ): Promise<CloudStorageList> {
    const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (verdict) qs.set('verdict', verdict);
    if (search) qs.set('search', search);
    const d = unwrap(await this.request('GET', `/admin/devices/cloud-storage?${qs}`));
    const c = d.counts ?? {};
    const cameras: CloudStorageCamera[] = (d.cameras ?? []).map((r: any) => ({
      id: r.id,
      name: r.name ?? '',
      serialNumber: r.serial_number ?? '',
      verdict: r.verdict,
      entitled: !!r.entitled,
      cloudMode: r.cloud_mode ?? 'off',
      cloudCap: r.cloud_cap ?? 0,
      retentionDays: r.retention_days ?? 0,
      online: !!r.online,
      eye4State: r.eye4_state || undefined,
      lastSignalAt: r.last_signal_at ?? null,
      lastClipAt: r.last_clip_at ?? null,
      clips7d: r.clips_7d ?? 0,
      planName: r.plan_name || undefined,
      planSlug: r.plan_slug || undefined,
      subscriptionStatus: r.subscription_status || undefined,
      ownerId: r.owner_id ?? '',
      ownerName: r.owner_name || undefined,
      ownerEmail: r.owner_email ?? '',
      ownerPhone: r.owner_phone || undefined,
    }));
    return {
      counts: {
        working: c.working ?? 0,
        onButSilent: c.on_but_silent ?? 0,
        cloudOff: c.cloud_off ?? 0,
        cameraOffline: c.camera_offline ?? 0,
        notEntitled: c.not_entitled ?? 0,
        total: c.total ?? 0,
      },
      cameras,
      pagination: {
        page: d.pagination?.page ?? page,
        pageSize: d.pagination?.page_size ?? pageSize,
        totalItems: d.pagination?.total_items ?? cameras.length,
        totalPages: d.pagination?.total_pages ?? 1,
      },
    };
  }

  /** Inline fix for a "cloud off" verdict: turn a camera's cloud recording
   *  on ('motion') or off. */
  async setCloudMode(serial: string, cloudMode: 'off' | 'motion' | 'continuous'): Promise<{ success: boolean }> {
    await this.request('POST', `/admin/devices/cloud-storage/${encodeURIComponent(serial)}`, { cloud_mode: cloudMode });
    return { success: true };
  }

  /** Activate (or disable) cloud recording across ALL of a customer's cameras at
   *  once. Returns how many cameras were updated. */
  async setCustomerCloudMode(userId: string, cloudMode: 'off' | 'motion' | 'continuous'): Promise<{ updated: number }> {
    const d = unwrap(await this.request('POST', `/admin/devices/cloud-storage/customer/${encodeURIComponent(userId)}`, { cloud_mode: cloudMode }));
    return { updated: d?.updated ?? 0 };
  }

  /** Cloud-region diagnostics: how many cameras are on each eye4 region, and the
   *  list of cameras on a non-US region (China clips can't be pulled into
   *  recordings). Powers the "affected cameras" section on the Cloud Storage tab. */
  async getCloudRegions(): Promise<CloudRegionsResult> {
    const d = unwrap(await this.request('GET', '/admin/devices/cloud-regions'));
    return {
      summary: (d?.summary ?? []).map((s: any) => ({
        region: s.region ?? '',
        regionLabel: s.region_label ?? '',
        digit: s.digit ?? '',
        cameras: s.cameras ?? 0,
        recordable: !!s.recordable,
      })),
      affected: (d?.affected ?? []).map((r: any) => ({
        serial: r.serial ?? '',
        name: r.name ?? '',
        ownerId: r.owner_id ?? '',
        ownerName: r.owner_name || undefined,
        ownerEmail: r.owner_email ?? '',
        region: r.region ?? '',
        regionLabel: r.region_label ?? '',
        recordable: !!r.recordable,
        lastEventAt: r.last_event_at ?? null,
        lastHydrated: !!r.last_hydrated,
      })),
    };
  }

  // ---- Cloud recordings viewer (ingest /v2/events + recording hydrate) --------
  /** Public media blob URL (unauthenticated proxy) for an <img>/<video> src. */
  mediaUrl(key: string): string {
    return `${API_BASE}/media?key=${encodeURIComponent(key)}`;
  }
  /** Days (YYYY-MM-DD) that have cloud recordings for a camera — the calendar. */
  async getRecordingDates(vuid: string): Promise<string[]> {
    const d = await this.request('GET', `/events/dates?vuid=${encodeURIComponent(vuid)}`);
    const list = (d && (d.dates ?? d)) as unknown[];
    return Array.isArray(list) ? list.map(String) : [];
  }
  /** A camera's events (a day's clips when date given), newest first. */
  async getCameraEvents(vuid: string, date?: string): Promise<RecordingEvent[]> {
    const qs = new URLSearchParams({ vuid });
    if (date) { qs.set('date', date); qs.set('limit', '2000'); } else { qs.set('limit', '200'); }
    const d = await this.request('GET', `/events?${qs}`);
    const list = (d?.events ?? []) as any[];
    return list.map((e) => ({
      id: (e.id ?? '').toString(),
      vuid: (e.vuid ?? '').toString(),
      type: (e.type ?? '').toString(),
      occurredAt: (e.occurred_at ?? '').toString(),
      pictureKey: (e.picture_key ?? '').toString(),
      videoKey: (e.video_key ?? '').toString(),
      posterKey: (e.poster_key ?? '').toString(),
      fileId: (e.file_id ?? '').toString(),
      pictureUrl: (e.picture_url ?? '').toString(),
    }));
  }
  /** Iris AI insights for one camera (admin) — detections vs noise, by label + hour. */
  async getCameraInsights(vuid: string, days = 30): Promise<CameraInsights> {
    const d = await this.request('GET', `/admin/alerts/insights?vuid=${encodeURIComponent(vuid)}&days=${days}`);
    return {
      sinceDays: d?.since_days ?? days,
      shown: d?.shown ?? 0,
      suppressed: d?.suppressed ?? 0,
      noisePct: d?.noise_pct ?? 0,
      byLabel: d?.by_label ?? {},
      byHour: Array.isArray(d?.by_hour) ? d.by_hour : [],
    };
  }

  /** Ask Iris a plain-English question about ONE camera (admin). Claude answers
   *  over the camera's detection metadata and may return snapshot evidence. */
  async askIris(
    vuid: string,
    question: string,
  ): Promise<{ answer: string; evidence: { label: string; at: string; picture: string }[] }> {
    const d = await this.request('POST', '/admin/alerts/ask', { vuid, question });
    return {
      answer: (d?.answer ?? '').toString(),
      evidence: Array.isArray(d?.evidence) ? d.evidence : [],
    };
  }

  /** Pull a file_id-only recording into a playable MP4 (by time). Returns true
   *  when hydrated — the event then has a videoKey on the next fetch. */
  async hydrateRecording(vuid: string, eventId: string, occurredAt: string): Promise<boolean> {
    const d = await this.request('POST', `/recording/eye4/${encodeURIComponent(vuid)}/hydrate`, {
      event_id: eventId, occurred_at: occurredAt,
    });
    return !!(d && d.hydrated);
  }

  /** Stitch a camera's clips for one day (YYYY-MM-DD) into a single MP4 with the
   *  capture time burned into each clip. Returns the media key (play via mediaUrl)
   *  and how many clips were combined. Throws ApiError (404) when the day has no
   *  playable clips to combine. */
  async buildDayReel(vuid: string, date: string): Promise<{ videoKey: string; clips: number }> {
    const d = await this.request('POST', `/recording/${encodeURIComponent(vuid)}/day-reel`, { date });
    return { videoKey: (d?.video_key ?? '').toString(), clips: Number(d?.clips ?? 0) };
  }

  /** Real fix #1: re-probe eye4 for the silent offline cameras (async job). */
  async refreshPresence(): Promise<{ started: boolean; toProcess: number; message: string }> {
    const d = unwrap(await this.request('POST', '/admin/devices/refresh-presence'));
    return { started: !!d.started, toProcess: d.to_process ?? 0, message: d.message ?? '' };
  }
  async getPresenceStatus(): Promise<any> {
    return unwrap(await this.request('GET', '/admin/devices/refresh-presence'));
  }

  /** Real fix #2: resolve empty vuids fleet-wide (async job). dryRun previews. */
  async backfillVuids(dryRun = false): Promise<{ started: boolean; toProcess: number; message: string }> {
    const d = unwrap(
      await this.request('POST', `/admin/devices/backfill-vuid${dryRun ? '?dry_run=true' : ''}`),
    );
    return { started: !!d.started, toProcess: d.to_process ?? 0, message: d.message ?? '' };
  }
  async getBackfillStatus(): Promise<any> {
    return unwrap(await this.request('GET', '/admin/devices/backfill-vuid'));
  }
  async getSimCards(): Promise<SimCard[]> {
    if (demoMode()) return MOCK_DEVICES.filter((d) => d.sim).map((d) => d.sim!);
    throw new NotImplementedError('GET /v2/admin/sim');
  }
  async topupSim(
    _simId: string,
    _amountUsd: number,
    _bundleMb: number,
    _reason: string,
  ): Promise<{ success: boolean; message: string }> {
    throw new NotImplementedError('POST /v2/admin/sim/topup');
  }
  async bulkTopupSims(
    _simIds: string[],
    _bundleMb: number,
    _reason: string,
  ): Promise<{ success: boolean; count: number }> {
    throw new NotImplementedError('POST /v2/admin/sim/topup');
  }
  async ackAlert(alertId: string, _annotation?: string): Promise<{ success: boolean }> {
    await this.request('POST', `/admin/alerts/${alertId}/ack`);
    return { success: true };
  }
  async getCustomer360(customerId: string): Promise<{
    customer: Customer;
    devices: CameraDevice[];
    subscriptions: Subscription[];
    payments: PaymentRecord[];
    sims: SimCard[];
  }> {
    if (demoMode()) {
      const customer = MOCK_CUSTOMERS.find((c) => c.id === customerId) ?? MOCK_CUSTOMERS[0];
      return {
        customer,
        devices: MOCK_DEVICES.filter((d) => d.ownerId === customer.id),
        subscriptions: MOCK_SUBSCRIPTIONS.filter((s) => s.customerId === customer.id),
        payments: MOCK_PAYMENTS.filter((p) => p.customerId === customer.id),
        sims: [],
      };
    }
    throw new NotImplementedError('GET /v2/admin/customers/{id}');
  }
  async mintImpersonationToken(
    _customerId: string,
    _reason: string,
  ): Promise<{ token: string; expiresAt: string }> {
    throw new NotImplementedError('POST /v2/admin/customers/{id}/impersonate');
  }

  /** Real customer detail: who they are, their cameras, their payments (incl.
   *  manually-recorded invoice payments), totals, subscription, block state. */
  async getCustomerDetail(customerId: string): Promise<CustomerDetail> {
    const d = unwrap(await this.request('GET', `/admin/customers/${customerId}`));
    return this.mapCustomerDetail(d);
  }

  /** Correct a customer's name and/or phone (e.g. add a number from an invoice).
   *  Only sends the fields provided; email is not editable (it's the login id). */
  async updateCustomer(
    customerId: string,
    patch: { firstName?: string; lastName?: string; phoneNumber?: string },
  ): Promise<CustomerDetail> {
    const body: Record<string, string> = {};
    if (patch.firstName !== undefined) body.first_name = patch.firstName;
    if (patch.lastName !== undefined) body.last_name = patch.lastName;
    if (patch.phoneNumber !== undefined) body.phone_number = patch.phoneNumber;
    const d = unwrap(await this.request('PATCH', `/admin/customers/${customerId}`, body));
    return this.mapCustomerDetail(d);
  }

  private mapCustomerDetail(d: any): CustomerDetail {
    const s = d?.subscription;
    return {
      id: d.id,
      email: d.email ?? '',
      firstName: d.first_name ?? undefined,
      lastName: d.last_name ?? undefined,
      phone: d.phone_number ?? undefined,
      isActive: !!d.is_active,
      isBlocked: !!d.is_blocked,
      createdAt: d.created_at ?? undefined,
      lastLoginAt: d.last_login_at ?? null,
      deviceCount: d.device_count ?? 0,
      hasPassword: !!d.has_password,
      loginGoogle: !!d.login_google,
      loginApple: !!d.login_apple,
      subscription: s
        ? {
            status: s.status ?? '',
            billingCycle: s.billing_cycle ?? undefined,
            planName: s.plan_name ?? undefined,
            planSlug: s.plan_slug ?? undefined,
            planPrice: s.plan_price ?? undefined,
            currency: s.currency ?? undefined,
            currentPeriodStart: s.current_period_start ?? undefined,
            currentPeriodEnd: s.current_period_end ?? undefined,
            cancelAtPeriodEnd: !!s.cancel_at_period_end,
            trialEndsAt: s.trial_ends_at ?? undefined,
            canceledAt: s.canceled_at ?? undefined,
          }
        : undefined,
      blocked: d.blocked
        ? { reason: d.blocked.reason ?? undefined, by: d.blocked.by ?? undefined, at: d.blocked.at ?? undefined }
        : undefined,
      cameras: (d.cameras ?? []).map((c: any) => ({
        id: c.id,
        name: c.name ?? '',
        serialNumber: c.serial_number ?? '',
        model: c.model ?? undefined,
        isOnline: !!c.is_online,
        lastSeenAt: c.last_seen_at ?? null,
        addedAt: c.added_at ?? undefined,
      })),
      payments: (d.payments ?? []).map((p: any) => ({
        id: p.id,
        amount: p.amount ?? 0,
        currency: p.currency ?? 'USD',
        status: p.status ?? '',
        type: p.type ?? undefined,
        provider: p.provider ?? undefined,
        externalId: p.external_id ?? undefined,
        notes: p.notes ?? undefined,
        invoiceUrl: p.invoice_url ?? undefined,
        createdAt: p.created_at ?? '',
      })),
      totals: {
        cameras: d.totals?.cameras ?? 0,
        camerasOnline: d.totals?.cameras_online ?? 0,
        payments: d.totals?.payments ?? 0,
        paidToDate: d.totals?.paid_to_date ?? 0,
        currency: d.totals?.currency ?? 'USD',
        pendingAmount: d.totals?.pending_amount ?? 0,
      },
      planHistory: (d.plan_history ?? []).map((h: any) => ({
        action: h.action ?? '',
        fromPlan: h.from_plan ?? undefined,
        toPlan: h.to_plan ?? undefined,
        by: h.by ?? undefined,
        createdAt: h.created_at ?? '',
      })),
    };
  }

  /** Set a new password for a customer (admin support action; email accounts). */
  async resetCustomerPassword(customerId: string, newPassword: string): Promise<{ success: boolean }> {
    await this.request('POST', `/admin/customers/${customerId}/reset-password`, { new_password: newPassword });
    return { success: true };
  }

  /** Message a customer directly. Opens an agent-initiated support thread, so the
   *  message lands in the customer's in-app Support inbox as a two-way conversation
   *  AND pushes their phone — reusing the support ticketing system. */
  async messageCustomer(customerId: string, subject: string, body: string): Promise<{ success: boolean }> {
    await this.request('POST', '/admin/support/tickets', { customer_id: customerId, subject, body });
    return { success: true };
  }

  /** Remove a camera from the platform (admin soft-delete, so it can be re-added). */
  async adminDeleteDevice(deviceId: string): Promise<{ success: boolean }> {
    await this.request('DELETE', `/admin/devices/${deviceId}`);
    return { success: true };
  }

  /** Block or unblock an account (real). Block needs a reason; unblock takes none. */
  async toggleCustomerBlock(
    customerId: string,
    isBlocked: boolean,
    reason: string,
  ): Promise<{ success: boolean }> {
    if (isBlocked) {
      await this.request('POST', `/admin/customers/${customerId}/block`, { reason });
    } else {
      await this.request('POST', `/admin/customers/${customerId}/unblock`);
    }
    return { success: true };
  }

  /** Restore a soft-deleted account (undo an accidental delete). Scoped
   *  server-side to a currently-deleted row, so it is safe to call and
   *  idempotent. The account's cameras, sharing and subscription are untouched
   *  by deletion, so this fully brings it back; the owner just logs in again. */
  async restoreCustomer(customerId: string): Promise<{ success: boolean }> {
    await this.request('POST', `/admin/customers/${customerId}/restore`);
    return { success: true };
  }

  async getSubscriptions(): Promise<Subscription[]> {
    if (demoMode()) return [...MOCK_SUBSCRIPTIONS];
    throw new NotImplementedError('GET /v2/admin/subscriptions');
  }
  async compSubscription(
    _customerId: string,
    _planSlug: string,
    _months: number,
    _reason: string,
  ): Promise<{ success: boolean; message: string }> {
    throw new NotImplementedError('POST /v2/admin/subscriptions/comp');
  }
  /** Record an invoice payment AND activate/renew the plan in one transaction
   *  (backend upserts the subscription to active + writes a provider:'manual'
   *  payment). This is the real invoice-based flow MWM runs on. amount must be
   *  > 0. Returns the resulting subscription. */
  async recordManualPayment(payment: {
    userId: string;
    planSlug: string;
    billingCycle: 'monthly' | 'yearly';
    amount: number;
    notes?: string;
  }): Promise<{ success: boolean }> {
    await this.request('POST', '/admin/subscriptions/manual-payment', {
      user_id: payment.userId,
      plan_slug: payment.planSlug,
      billing_cycle: payment.billingCycle,
      amount: payment.amount,
      notes: payment.notes ?? '',
    });
    return { success: true };
  }
  async refundPayment(_paymentId: string, _reason: string): Promise<{ success: boolean }> {
    throw new NotImplementedError('POST /v2/admin/payments/{id}/refund');
  }
  /** Edit a plan (real, PUT /v2/plans/{slug} — reached directly through Caddy).
   *  Only the fields provided are sent; `features`, when given, REPLACES the whole
   *  feature set, so callers must pass the complete list. */
  async updatePlan(
    slug: string,
    updates: {
      priceMonthly?: number;
      priceYearly?: number;
      isActive?: boolean;
      name?: string;
      description?: string;
      features?: { key: string; value: string }[];
    },
  ): Promise<{ success: boolean }> {
    const body: Record<string, unknown> = {};
    if (updates.priceMonthly !== undefined) body.price_monthly = updates.priceMonthly;
    if (updates.priceYearly !== undefined) body.price_yearly = updates.priceYearly;
    if (updates.isActive !== undefined) body.is_active = updates.isActive;
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.description !== undefined) body.description = updates.description;
    if (updates.features !== undefined) {
      body.features = updates.features.map((f) => ({ feature_key: f.key, feature_value: f.value }));
    }
    await this.request('PUT', `/plans/${slug}`, body);
    return { success: true };
  }
  /** Edit an inventory item (real, PUT /v2/devices/inventory/{id}). The backend
   *  accepts quantity, price and low_stock_threshold; only what's provided is sent. */
  async updateInventoryItem(
    id: string,
    updates: { quantityOnHand?: number; retailPriceUsd?: number; lowStockThreshold?: number },
  ): Promise<{ success: boolean }> {
    const body: Record<string, number> = {};
    if (updates.quantityOnHand !== undefined) body.quantity = updates.quantityOnHand;
    if (updates.retailPriceUsd !== undefined) body.price = updates.retailPriceUsd;
    if (updates.lowStockThreshold !== undefined) body.low_stock_threshold = updates.lowStockThreshold;
    await this.request('PUT', `/devices/inventory/${id}`, body);
    return { success: true };
  }
  /** Add a new camera model to the catalog. Returns the new item id. */
  async createInventoryItem(payload: {
    modelName: string; sku: string; type: string; price: number;
    quantity: number; lowStockThreshold: number; description?: string; features?: string[];
  }): Promise<string> {
    const d = unwrap(await this.request('POST', '/devices/inventory', {
      model_name: payload.modelName,
      sku: payload.sku,
      type: payload.type,
      price: payload.price,
      quantity: payload.quantity,
      low_stock_threshold: payload.lowStockThreshold,
      description: payload.description ?? '',
      features: payload.features ?? [],
    })) as any;
    return String(d?.id ?? '');
  }
  /** Upload a model's product image (multipart). Returns the public image URL. */
  async uploadInventoryImage(id: string, file: File): Promise<string> {
    const fd = new FormData();
    fd.append('image', file);
    // Multipart: do NOT set Content-Type (the browser adds the boundary).
    const res = await fetch(`${API_BASE}/devices/inventory/${encodeURIComponent(id)}/image`, {
      method: 'POST',
      headers: this.access ? { Authorization: `Bearer ${this.access}` } : {},
      body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, 'Image upload failed');
    const j = await res.json().catch(() => ({}));
    return unwrap(j)?.url ?? '';
  }
  // Support agent console — real device-service backend.
  async getTickets(status?: SupportTicketStatus): Promise<{ tickets: SupportTicket[]; openCount: number }> {
    if (demoMode()) return { tickets: [], openCount: 0 };
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const d = unwrap(await this.request('GET', `/admin/support/tickets${qs}`));
    return {
      tickets: (d.tickets ?? []).map(mapTicket),
      openCount: d.open_count ?? 0,
    };
  }
  async getTicket(id: string): Promise<SupportTicket> {
    return mapTicket(unwrap(await this.request('GET', `/admin/support/tickets/${id}`)));
  }
  async replyTicket(ticketId: string, body: string): Promise<SupportTicketMessage> {
    const m = unwrap(await this.request('POST', `/admin/support/tickets/${ticketId}/messages`, { body }));
    return mapTicketMessage(m);
  }
  async setTicketStatus(
    ticketId: string,
    change: { status?: SupportTicketStatus; assignedAgentId?: string },
  ): Promise<SupportTicket> {
    const payload: Record<string, unknown> = {};
    if (change.status) payload.status = change.status;
    if (change.assignedAgentId) payload.assigned_agent_id = change.assignedAgentId;
    return mapTicket(unwrap(await this.request('POST', `/admin/support/tickets/${ticketId}/status`, payload)));
  }
  async getEmergencies(): Promise<EmergencyEvent[]> {
    if (demoMode()) return [...MOCK_EMERGENCIES];
    throw new NotImplementedError('GET /v2/admin/emergencies');
  }
  async updateEmergencyStatus(
    _emergencyId: string,
    _status: EmergencyEvent['status'],
    _notes: string,
  ): Promise<{ success: boolean }> {
    throw new NotImplementedError('POST /v2/admin/emergencies/{id}/status');
  }
  // Platform staff — real auth-service backend (/v2/admin/admins).
  async getAdmins(): Promise<StaffAdmin[]> {
    if (demoMode()) return [];
    const rows = unwrap(await this.request('GET', '/admin/admins')) as any[];
    return (rows ?? []).map((r) => ({
      id: r.id,
      email: r.email,
      name: r.name || r.email,
      role: r.role as PlatformRole,
      mfaEnabled: !!r.mfa_enabled,
      isActive: r.is_active !== false,
      lastLogin: r.last_login || undefined,
      createdAt: r.created_at,
    }));
  }
  // Grant a platform role to an EXISTING account (by email). The person must
  // already have a MyWatchman account; this promotes them to staff.
  async createAdmin(email: string, role: PlatformRole): Promise<{ success: boolean }> {
    await this.request('POST', '/admin/admins', { email, role });
    return { success: true };
  }
  /** Create a brand-new staff login (name/email/password) + assign a role. */
  async createStaffAccount(name: string, email: string, password: string, role: PlatformRole): Promise<{ success: boolean }> {
    await this.request('POST', '/admin/admins/create', { name, email, password, role });
    return { success: true };
  }
  async updateAdminRole(id: string, role: PlatformRole): Promise<{ success: boolean }> {
    await this.request('PUT', `/admin/admins/${id}/role`, { role });
    return { success: true };
  }
  async revokeAdmin(id: string): Promise<{ success: boolean }> {
    await this.request('DELETE', `/admin/admins/${id}`);
    return { success: true };
  }
  // Revenue & fleet analytics — real subscription-service backend.
  async getAnalytics(): Promise<AnalyticsData> {
    const d = unwrap(await this.request('GET', '/admin/analytics'));
    return {
      mrr: d.mrr ?? 0,
      activeSubscriptions: d.active_subscriptions ?? 0,
      payingAccounts: d.paying_accounts ?? 0,
      arpu: d.arpu ?? 0,
      paidToDate: d.paid_to_date ?? 0,
      pendingAmount: d.pending_amount ?? 0,
      hardwareCollected: d.hardware_collected ?? 0,
      hardwarePending: d.hardware_pending ?? 0,
      hardwareOrders: d.hardware_orders ?? 0,
      totalCollected: d.total_collected ?? 0,
      failed30d: d.failed_30d ?? 0,
      succeeded30d: d.succeeded_30d ?? 0,
      paymentFailureRate: d.payment_failure_rate ?? 0,
      canceled90d: d.canceled_90d ?? 0,
      customers: d.customers ?? 0,
      currency: d.currency ?? 'USD',
      planBreakdown: (d.plan_breakdown ?? []).map((p: any) => ({
        plan: p.plan, slug: p.slug, subscribers: p.subscribers ?? 0, mrr: p.mrr ?? 0,
      })),
      revenueTrend: (d.revenue_trend ?? []).map((r: any) => ({
        month: r.month, revenue: r.revenue ?? 0,
      })),
      fleet: {
        total: d.fleet?.total ?? 0,
        online: d.fleet?.online ?? 0,
        offline: d.fleet?.offline ?? 0,
      },
    };
  }

  // Customer broadcasts — real alert-service backend (/v2/admin/broadcasts).
  async getBroadcasts(): Promise<BroadcastMessage[]> {
    if (demoMode()) return [];
    const rows = unwrap(await this.request('GET', '/admin/broadcasts')) as any[];
    return (rows ?? []).map(mapBroadcast);
  }
  // Reach preview: how many devices the chosen segment reaches right now.
  async getBroadcastAudience(segment?: BroadcastSegment): Promise<number> {
    if (demoMode()) return 0;
    const qs = new URLSearchParams();
    if (segment && segment.type !== 'all') {
      qs.set('segment_type', segment.type);
      if (segment.value) qs.set('segment_value', segment.value);
    }
    const path = qs.toString() ? `/admin/broadcasts/audience?${qs}` : '/admin/broadcasts/audience';
    const d = unwrap(await this.request('GET', path));
    return d?.recipient_count ?? 0;
  }
  async sendBroadcast(title: string, body: string, segment?: BroadcastSegment): Promise<BroadcastMessage> {
    const seg = segment && segment.type !== 'all' ? { type: segment.type, value: segment.value } : { type: 'all', value: '' };
    return mapBroadcast(unwrap(await this.request('POST', '/admin/broadcasts', { title, body, segment: seg })));
  }

  // The unified admin audit trail: operator actions on customers (edit, block,
  // unblock, password reset) unioned with subscription/billing lifecycle events,
  // newest first. Real backend data from GET /v2/admin/audit-log.
  async getAuditLogs(limit = 200, offset = 0): Promise<AuditLogEntry[]> {
    if (demoMode()) return [];
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const rows = unwrap(await this.request('GET', `/admin/audit-log?${qs}`)) as any[];
    return (rows ?? []).map((r) => ({
      id: r.id,
      createdAt: r.created_at,
      action: r.action,
      source: (r.source ?? 'system') as AuditLogEntry['source'],
      targetType: r.target_type ?? '',
      targetId: r.target_id || undefined,
      // A device row's resource_id is the camera, not the customer, so the
      // owner email rides in metadata — fall back to it so the affected
      // customer still shows for deletes.
      targetEmail: r.target_email || r.metadata?.owner_email || undefined,
      targetName: r.target_name || undefined,
      actorId: r.actor_id || undefined,
      actorEmail: r.actor_email || undefined,
      actorName: r.actor_name || undefined,
      metadata: r.metadata ?? null,
    }));
  }

  /** AI usage this calendar month + per-customer breakdown (with each cap). */
  async getAIUsage(): Promise<AIUsageSummary> {
    const d = unwrap(await this.request('GET', '/admin/ai/usage')) as any;
    const rows = (d?.by_user ?? []) as any[];
    return {
      requests: Number(d?.requests ?? 0),
      inputTokens: Number(d?.input_tokens ?? 0),
      outputTokens: Number(d?.output_tokens ?? 0),
      cacheReadTokens: Number(d?.cache_read_tokens ?? 0),
      costUsd: Number(d?.cost_usd ?? 0),
      byUser: rows.map((r) => ({
        userId: String(r.user_id ?? ''),
        email: String(r.email ?? ''),
        name: String(r.name ?? ''),
        planSlug: String(r.plan_slug ?? ''),
        planName: String(r.plan_name ?? ''),
        planCredits: r.plan_credits == null ? null : Number(r.plan_credits),
        requests: Number(r.requests ?? 0),
        inputTokens: Number(r.input_tokens ?? 0),
        outputTokens: Number(r.output_tokens ?? 0),
        cacheReadTokens: Number(r.cache_read_tokens ?? 0),
        costUsd: Number(r.cost_usd ?? 0),
        lastUsedAt: r.last_used_at == null ? null : String(r.last_used_at),
        monthlyCap: r.monthly_cap == null ? null : Number(r.monthly_cap),
      })),
    };
  }

  /** Set a customer's monthly AI request cap; pass null to remove it (unlimited). */
  async setAILimit(userId: string, cap: number | null): Promise<void> {
    await this.request('PUT', `/admin/ai/limits/${encodeURIComponent(userId)}`, {
      monthly_request_cap: cap == null ? -1 : cap,
    });
  }

  /** In-app hardware orders (Eye-series cameras + plans), newest first. */
  async getStoreOrders(status?: string): Promise<StoreOrderList> {
    const qs = new URLSearchParams({ limit: '200' });
    if (status && status !== 'all') qs.set('status', status);
    const d = unwrap(await this.request('GET', `/admin/store/orders?${qs}`)) as any;
    return {
      total: Number(d?.total ?? 0),
      byStatus: (d?.by_status ?? {}) as Record<string, number>,
      totalValue: Number(d?.total_value ?? 0),
      orders: ((d?.orders ?? []) as any[]).map((o) => ({
        id: String(o.id ?? ''),
        status: (o.status ?? 'pending') as StoreOrder['status'],
        currency: String(o.currency ?? 'USD'),
        subtotal: Number(o.subtotal ?? 0),
        gst: Number(o.gst ?? 0),
        total: Number(o.total ?? 0),
        items: Array.isArray(o.items) ? o.items : [],
        plans: Array.isArray(o.plans) ? o.plans : [],
        customerName: String(o.customer_name ?? ''),
        customerPhone: String(o.customer_phone ?? ''),
        customerAddress: String(o.customer_address ?? ''),
        email: String(o.email ?? ''),
        createdAt: String(o.created_at ?? ''),
      })),
    };
  }

  /** Set an order's status (mark a cash/manual order paid, cancel one, etc.). */
  async setStoreOrderStatus(id: string, status: string): Promise<void> {
    await this.request('PUT', `/admin/store/orders/${encodeURIComponent(id)}/status`, { status });
  }

  /** The fleet grouped by camera version (model): per version, how many cameras
   *  and how many are reporting, dark, or have never reported. A version going
   *  dark together is a firmware/hardware signal; one dark camera is a ticket. */
  async getFleetVersions(): Promise<FleetVersionSummary> {
    const d = unwrap(await this.request('GET', '/admin/devices/versions'));
    const versions: FleetVersion[] = (d.versions ?? []).map((v: any) => ({
      model: String(v.model ?? 'Unknown'),
      cameras: Number(v.cameras ?? 0),
      online: Number(v.online ?? 0),
      offline: Number(v.offline ?? 0),
      neverReported: Number(v.never_reported ?? 0),
      lastSeenAt: v.last_seen_at ?? null,
    }));
    return {
      versions,
      totals: {
        versions: Number(d.totals?.versions ?? versions.length),
        cameras: Number(d.totals?.cameras ?? 0),
        online: Number(d.totals?.online ?? 0),
      },
    };
  }
}

export const api = new ApiClient();
